import React, { forwardRef, useMemo } from "react";
import {
  matchPath as routerMatchPath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

const buildHistory = (navigate, location) => ({
  push: (to, state, options) => navigate(to, { state, ...options }),
  replace: (to, state, options) => navigate(to, { replace: true, state, ...options }),
  go: (delta) => navigate(delta),
  goBack: () => navigate(-1),
  goForward: () => navigate(1),
  location,
  length: globalThis.history.length,
});

export const withRouterLegacy = (Component) => {
  const ComponentWithRouter = forwardRef((props, ref) => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    const history = useMemo(
      () => buildHistory(navigate, location),
      [navigate, location]
    );

    const match = useMemo(
      () => ({
        params,
        path: location.pathname,
        url: location.pathname,
        isExact: true,
      }),
      [location.pathname, params]
    );

    return (
      <Component
        {...props}
        ref={ref}
        history={history}
        location={location}
        match={match}
      />
    );
  });

  const componentName = Component.displayName || Component.name || "Component";
  ComponentWithRouter.displayName = `withRouterLegacy(${componentName})`;

  return ComponentWithRouter;
};

export const legacyMatchPath = (pathname, options) => {
  if (!options) {
    return null;
  }

  if (typeof options === "string") {
    return routerMatchPath(options, pathname);
  }

  const { path, exact = false, sensitive = false } = options;
  if (!path) {
    return null;
  }

  return routerMatchPath(
    { path, caseSensitive: sensitive, end: exact },
    pathname
  );
};
